import type {
  RichText,
  RichTextInline,
  RichTextMark,
  TextColorToken,
} from '@matter_extractor/shared';

/**
 * Le modèle du texte riche, la barre qui l'édite, et le pont vers le DOM
 * (refonte modulaire §9.2, H28, H29; maquettes `Inspecteurs`,
 * `EditeurPageEcran`; issue #58).
 *
 * §9.2 asks for a WYSIWYG - « on voit les mots en gras, en italique, soulignés
 * ou colorés, jamais des marques » - over a tree §54 has already closed:
 * paragraphs, bullet lists, three marks, three theme colours, line breaks, and
 * nothing else. What is missing between the two is this file.
 *
 * ## Le modèle est l'arbre, le DOM n'est qu'une vue
 *
 * Nothing here ever parses HTML that came from outside. The surface is a
 * `contentEditable` painted from the model by `renderRichText` - the SAME pure
 * function the reader paints with (§9.2: « il n'existe pas deux orthographes du
 * même texte ») - and read back by `readRichText`, which walks the DOM and
 * keeps only the nodes the model knows. Everything else is transparent: a
 * `<span style="font-weight:700">` a browser invented, a `<font>` a paste
 * carried, a `<table>` somebody dropped in - the walker descends into it and
 * keeps the text. So there is no sanitiser to get wrong, because there is no
 * parser to attack: what is not in the closed list simply has no spelling in
 * the model.
 *
 * A paste is intercepted anyway and reinserted as plain text, which is a
 * different guarantee and worth both: the walker would keep the words of a
 * pasted document, and the operator would silently lose its formatting on the
 * next repaint. Refusing the markup at the door is what makes that loss
 * predictable.
 *
 * ## Les offsets, et pourquoi ils sont la monnaie de ce fichier
 *
 * A selection lives in the DOM; a mark lives in the model. Every operation
 * below therefore takes a RANGE IN PLAIN CHARACTERS - the offsets of
 * `richTextPlain`, the very string H23 measures its 4000 characters on - and
 * `plainRangeOf` / `selectPlainRange` are the two functions that cross. One
 * currency, and the caret survives a repaint because the repaint restores the
 * same numbers.
 *
 * ## Les blocs, la forme interne
 *
 * A document is nodes, and a bullet list is a node holding items. For editing
 * that shape is in the way: a range that starts in a paragraph and ends in the
 * second item of a list touches THREE lines, and the operations want lines.
 * So `toBlocks` flattens to one block per line - `{ list, children }` - and
 * `fromBlocks` groups the consecutive list blocks back into one `bullet-list`.
 * The flattening is exactly the one `richTextPlain` performs, which is what
 * makes the offsets agree.
 */

/** A range of PLAIN characters, the currency of every operation here. */
export type PlainRange = { readonly from: number; readonly to: number };

/** One line of the document while it is being edited - see the head. */
export type EditorBlock = {
  readonly list: boolean;
  readonly children: readonly RichTextInline[];
};

const MARK_ORDER: readonly RichTextMark[] = ['bold', 'italic', 'underline'];

/* -------------------------------------------------------------- the blocks */

export function toBlocks(document: RichText): EditorBlock[] {
  return document.nodes.flatMap<EditorBlock>((node) =>
    node.type === 'paragraph'
      ? [{ list: false, children: node.children }]
      : node.items.map((item) => ({ list: true, children: item.children })),
  );
}

export function fromBlocks(blocks: readonly EditorBlock[]): RichText {
  const nodes: RichText['nodes'] = [];
  for (const block of blocks) {
    if (!block.list) {
      nodes.push({ type: 'paragraph', children: [...block.children] });
      continue;
    }
    const last = nodes[nodes.length - 1];
    if (last !== undefined && last.type === 'bullet-list') {
      last.items.push({ children: [...block.children] });
      continue;
    }
    nodes.push({ type: 'bullet-list', items: [{ children: [...block.children] }] });
  }
  return { nodes };
}

/** How many plain characters one inline is worth: a break is one newline. */
function inlineLength(inline: RichTextInline): number {
  return inline.type === 'break' ? 1 : inline.text.length;
}

function blockLength(block: EditorBlock): number {
  return block.children.reduce((total, inline) => total + inlineLength(inline), 0);
}

/** Where each block starts in the plain string, blocks being joined by « \n ». */
export function blockStarts(blocks: readonly EditorBlock[]): number[] {
  const starts: number[] = [];
  let at = 0;
  for (const block of blocks) {
    starts.push(at);
    at += blockLength(block) + 1;
  }
  return starts;
}

/** The plain text of a document - `richTextPlain` restated over the blocks. */
export function plainOf(document: RichText): string {
  return toBlocks(document)
    .map((block) =>
      block.children.map((inline) => (inline.type === 'break' ? '\n' : inline.text)).join(''),
    )
    .join('\n');
}

/* ------------------------------------------------------------- normalising */

const sameMarks = (a: readonly RichTextMark[], b: readonly RichTextMark[]): boolean =>
  a.length === b.length && a.every((mark) => b.includes(mark));

/** Marks in ONE order, so two runs that carry the same three compare equal. */
function orderedMarks(marks: readonly RichTextMark[]): RichTextMark[] {
  return MARK_ORDER.filter((mark) => marks.includes(mark));
}

/**
 * Drop the empty runs and merge the neighbours that agree.
 *
 * Called after every operation, because every operation splits: without it a
 * paragraph gathers one run per gesture and two documents that read the same
 * would compare different - which is what `structurallyEqual` greys the save
 * button on (§4).
 */
export function normalizeRichText(document: RichText): RichText {
  const blocks = toBlocks(document).map((block) => {
    const merged: RichTextInline[] = [];
    for (const inline of block.children) {
      if (inline.type === 'text') {
        if (inline.text === '') continue;
        const previous = merged[merged.length - 1];
        if (
          previous !== undefined &&
          previous.type === 'text' &&
          previous.color === inline.color &&
          sameMarks(previous.marks, inline.marks)
        ) {
          merged[merged.length - 1] = { ...previous, text: previous.text + inline.text };
          continue;
        }
        merged.push({ ...inline, marks: orderedMarks(inline.marks) });
        continue;
      }
      merged.push(inline);
    }
    return { list: block.list, children: merged };
  });
  return fromBlocks(blocks);
}

/* --------------------------------------------------------------- the marks */

/** One run of a block, with its plain range - what an operation cuts on. */
type Piece = { readonly inline: RichTextInline; readonly start: number; readonly end: number };

function piecesOf(block: EditorBlock, blockStart: number): Piece[] {
  const pieces: Piece[] = [];
  let at = blockStart;
  for (const inline of block.children) {
    const end = at + inlineLength(inline);
    pieces.push({ inline, start: at, end });
    at = end;
  }
  return pieces;
}

/**
 * Rewrite the text runs a range covers, splitting at its two edges.
 *
 * The workhorse of `toggleMark` and `setColor`. A `break` is never touched - it
 * carries no mark and no colour, and cutting one in half has no meaning.
 */
function mapRange(
  document: RichText,
  range: PlainRange,
  rewrite: (inline: Extract<RichTextInline, { type: 'text' }>) => RichTextInline,
): RichText {
  const blocks = toBlocks(document);
  const starts = blockStarts(blocks);
  const next = blocks.map((block, index) => {
    const children: RichTextInline[] = [];
    for (const piece of piecesOf(block, starts[index] ?? 0)) {
      const { inline, start, end } = piece;
      if (inline.type !== 'text' || end <= range.from || start >= range.to) {
        children.push(inline);
        continue;
      }
      const head = Math.max(range.from - start, 0);
      const tail = Math.min(range.to - start, inline.text.length);
      if (head > 0) children.push({ ...inline, text: inline.text.slice(0, head) });
      children.push(rewrite({ ...inline, text: inline.text.slice(head, tail) }));
      if (tail < inline.text.length) children.push({ ...inline, text: inline.text.slice(tail) });
    }
    return { list: block.list, children };
  });
  return normalizeRichText(fromBlocks(next));
}

/** Every text run a range covers, in reading order. Empty on a collapsed one. */
function coveredRuns(
  document: RichText,
  range: PlainRange,
): Extract<RichTextInline, { type: 'text' }>[] {
  const blocks = toBlocks(document);
  const starts = blockStarts(blocks);
  const covered: Extract<RichTextInline, { type: 'text' }>[] = [];
  blocks.forEach((block, index) => {
    for (const piece of piecesOf(block, starts[index] ?? 0)) {
      if (piece.inline.type !== 'text') continue;
      if (piece.end <= range.from || piece.start >= range.to) continue;
      covered.push(piece.inline);
    }
  });
  return covered;
}

/**
 * B, I, U - added when the range does not already carry the mark throughout,
 * removed when it does.
 *
 * The « throughout » is what makes the button a TOGGLE rather than a switch: a
 * selection half bold reads as not bold, so one press makes all of it bold and
 * the second clears it. Anything else needs two presses to do one thing.
 */
export function toggleMark(document: RichText, range: PlainRange, mark: RichTextMark): RichText {
  if (range.from >= range.to) return document;
  const covered = coveredRuns(document, range);
  if (covered.length === 0) return document;
  const all = covered.every((run) => run.marks.includes(mark));
  return mapRange(document, range, (run) => ({
    ...run,
    marks: orderedMarks(all ? run.marks.filter((one) => one !== mark) : [...run.marks, mark]),
  }));
}

/** The colour of a passage, among the theme's three, or null to clear it (H28). */
export function setColor(
  document: RichText,
  range: PlainRange,
  color: TextColorToken | null,
): RichText {
  if (range.from >= range.to) return document;
  return mapRange(document, range, (run) => ({ ...run, color }));
}

/** Which of the three marks the whole range carries - the pressed state of B, I, U. */
export function activeMarks(document: RichText, range: PlainRange): RichTextMark[] {
  const covered = coveredRuns(document, range);
  if (covered.length === 0) return [];
  return MARK_ORDER.filter((mark) => covered.every((run) => run.marks.includes(mark)));
}

/** The colour the whole range carries, or null when it carries none or several. */
export function activeColor(document: RichText, range: PlainRange): TextColorToken | null {
  const covered = coveredRuns(document, range);
  const first = covered[0];
  if (first === undefined) return null;
  return covered.every((run) => run.color === first.color) ? first.color : null;
}

/**
 * « Liste à puces » - the blocks the range touches become items, or go back to
 * paragraphs when they already are.
 *
 * The whole block and never a passage: §9.2 puts the bullet list among the
 * NODES of the tree, so a half-listed paragraph has no spelling.
 */
export function toggleBulletList(document: RichText, range: PlainRange): RichText {
  const blocks = toBlocks(document);
  if (blocks.length === 0) return document;
  const starts = blockStarts(blocks);
  const touched = blocks
    .map((block, index) => ({ block, index, start: starts[index] ?? 0 }))
    .filter(({ block, start }) => start <= range.to && start + blockLength(block) >= range.from);
  if (touched.length === 0) return document;
  const all = touched.every(({ block }) => block.list);
  const wanted = new Set(touched.map(({ index }) => index));
  return fromBlocks(
    blocks.map((block, index) => (wanted.has(index) ? { ...block, list: !all } : block)),
  );
}

/**
 * Replace a range with text - what a paste, the `{ }` button and the `@` menu
 * all go through.
 *
 * The inserted text inherits the run it LANDS IN, which is what makes inserting
 * a variable inside a bold sentence keep the sentence bold. A `\n` becomes a
 * `break` and never a new paragraph: Entrée is the browser's gesture and
 * belongs to the DOM, while a pasted line feed is text somebody wants kept.
 */
export function insertText(
  document: RichText,
  range: PlainRange,
  text: string,
): { readonly document: RichText; readonly caret: number } {
  const blocks = toBlocks(document);
  const starts = blockStarts(blocks);
  /*
    The run the insertion lands in - the last one that STARTS at or before the
    caret, so a caret at the boundary of two runs continues the first rather
    than adopting the next. That is what an operator expects: the marks they
    just finished typing in.
  */
  let style: { marks: readonly RichTextMark[]; color: TextColorToken | null } = {
    marks: [],
    color: null,
  };
  blocks.forEach((block, index) => {
    for (const piece of piecesOf(block, starts[index] ?? 0)) {
      if (piece.inline.type !== 'text') continue;
      if (piece.start > range.from) continue;
      style = { marks: piece.inline.marks, color: piece.inline.color };
    }
  });

  const inserted: RichTextInline[] = text
    .split('\n')
    .flatMap((part, index) => [
      ...(index === 0 ? [] : [{ type: 'break' as const }]),
      { type: 'text' as const, text: part, marks: orderedMarks(style.marks), color: style.color },
    ]);

  const next = blocks.map((block, index) => {
    const blockStart = starts[index] ?? 0;
    const children: RichTextInline[] = [];
    let placed = false;
    for (const piece of piecesOf(block, blockStart)) {
      const { inline, start, end } = piece;
      if (inline.type !== 'text') {
        // A break inside the cut range goes with it; outside, it stays.
        if (start >= range.from && end <= range.to && range.from < range.to) continue;
        children.push(inline);
        continue;
      }
      const head = inline.text.slice(
        0,
        Math.max(0, Math.min(range.from - start, inline.text.length)),
      );
      const tail = inline.text.slice(Math.max(0, Math.min(range.to - start, inline.text.length)));
      const touches = range.from <= end && range.to >= start;
      if (!touches) {
        children.push(inline);
        continue;
      }
      if (head !== '') children.push({ ...inline, text: head });
      if (!placed && range.from >= start && range.from <= end) {
        children.push(...inserted);
        placed = true;
      }
      if (tail !== '') children.push({ ...inline, text: tail });
    }
    /*
      An EMPTY block the caret sits in holds no run to land in, so the insertion
      has nowhere to be placed by the loop above - a fresh Texte block is
      exactly that, and it is the most common case of all.
    */
    if (!placed && range.from >= blockStart && range.from <= blockStart + blockLength(block)) {
      children.push(...inserted);
    }
    return { list: block.list, children };
  });

  const document_ =
    blocks.length === 0 ? fromBlocks([{ list: false, children: inserted }]) : fromBlocks(next);
  return { document: normalizeRichText(document_), caret: range.from + text.length };
}

/* ------------------------------------------------------------------ the `@` */

/** The `@query` the caret sits at the end of, or null. The protocol's rule (§8.6). */
export function atQueryBefore(text: string): string | null {
  const match = /@([^\s@]*)$/.exec(text);
  return match === null ? null : (match[1] ?? '');
}

/* ------------------------------------------------------------- the DOM view */

/** What one walk of the editable surface answers: the model, and the offsets. */
export type EditableScan = {
  readonly document: RichText;
  /** Where every visited node begins and ends, in plain characters. */
  readonly starts: Map<Node, number>;
  readonly ends: Map<Node, number>;
  /** Every text node in reading order, with its plain start. */
  readonly texts: readonly { readonly node: Text; readonly start: number }[];
  /**
   * Every PILL in reading order, with the range of its token (§8.6, D5).
   *
   * A pill is an atom the caret cannot enter, and it is worth the LENGTH OF ITS
   * TOKEN in plain characters - `{{champ:<id>}}`, not `@prénom` - so the
   * offsets here agree with `richTextPlain`, which measures the stored text.
   * The two maps above hold the pill's own bounds; this list is what lets a
   * caret aimed inside a token land on the nearer edge instead.
   */
  readonly atoms: readonly {
    readonly node: Element;
    readonly start: number;
    readonly end: number;
  }[];
};

const MARK_OF_TAG: Readonly<Record<string, RichTextMark>> = {
  STRONG: 'bold',
  B: 'bold',
  EM: 'italic',
  I: 'italic',
  U: 'underline',
  INS: 'underline',
};

const COLORS: readonly string[] = ['text', 'muted', 'accent'];

/** A `div` a browser made on Entrée is a paragraph; so is a `p`, so is an `li`. */
const BLOCK_TAGS = new Set(['P', 'LI', 'DIV']);

/**
 * ONE walk of the editable surface, producing the model AND the offset map.
 *
 * One walk and not two, and that is the whole reason this type exists: the
 * caret is restored at a number the model was read at, so a second traversal
 * that counted a stray node differently would put the caret a character off -
 * once per repaint, silently, and only on the documents that hold whatever the
 * two walkers disagreed about.
 */
export function scanEditable(root: HTMLElement): EditableScan {
  const blocks: { list: boolean; children: RichTextInline[] }[] = [];
  const starts = new Map<Node, number>();
  const ends = new Map<Node, number>();
  const texts: { node: Text; start: number }[] = [];
  const atoms: { node: Element; start: number; end: number }[] = [];
  let at = 0;
  let current: { list: boolean; children: RichTextInline[] } | null = null;

  function open(list: boolean): void {
    // Every block after the first is preceded by the « \n » that joins them.
    if (blocks.length > 0) at += 1;
    current = { list, children: [] };
    blocks.push(current);
  }

  function walk(
    node: Node,
    marks: readonly RichTextMark[],
    color: TextColorToken | null,
    inList: boolean,
  ): void {
    for (const child of Array.from(node.childNodes)) {
      starts.set(child, at);
      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent ?? '';
        if (text !== '') {
          if (current === null) open(inList);
          current?.children.push({ type: 'text', text, marks: orderedMarks(marks), color });
          texts.push({ node: child as Text, start: at });
          at += text.length;
        }
        ends.set(child, at);
        continue;
      }
      if (!(child instanceof Element)) {
        ends.set(child, at);
        continue;
      }
      const tag = child.tagName;
      const pill = child.getAttribute('data-variable');
      if (pill !== null) {
        /*
          A pill reads back as its TOKEN, in the marks of the run it sits in -
          which is what keeps « une variable dans une phrase en gras reste en
          gras » true through a round trip. Its inner text (`@prénom`) is never
          visited: it is not in `texts`, so no caret is ever placed inside it,
          and its bounds are recorded on every descendant so a selection that
          did land there is read as the pill's own edge.
        */
        const token = `{{${pill}}}`;
        if (current === null) open(inList);
        current?.children.push({ type: 'text', text: token, marks: orderedMarks(marks), color });
        const end = at + token.length;
        atoms.push({ node: child, start: at, end });
        for (const inner of Array.from(child.querySelectorAll('*'))) {
          starts.set(inner, at);
          ends.set(inner, end);
        }
        for (const inner of Array.from(child.childNodes)) {
          starts.set(inner, at);
          ends.set(inner, end);
        }
        at = end;
      } else if (tag === 'BR') {
        if (current === null) open(inList);
        current?.children.push({ type: 'break' });
        at += 1;
      } else if (BLOCK_TAGS.has(tag) && !hasBlockDescendant(child)) {
        open(tag === 'LI' || inList);
        walk(child, marks, color, tag === 'LI' || inList);
        current = null;
      } else if (tag === 'UL' || tag === 'OL') {
        walk(child, marks, color, true);
      } else if (MARK_OF_TAG[tag] !== undefined) {
        const mark = MARK_OF_TAG[tag];
        walk(child, mark === undefined ? marks : [...marks, mark], color, inList);
      } else if (tag === 'SPAN' && COLORS.includes(child.getAttribute('data-color') ?? '')) {
        walk(child, marks, child.getAttribute('data-color') as TextColorToken, inList);
      } else {
        // Transparent: anything the model does not know keeps only its text.
        walk(child, marks, color, inList);
      }
      ends.set(child, at);
    }
  }

  starts.set(root, 0);
  walk(root, [], null, false);
  ends.set(root, at);

  return { document: normalizeRichText(fromBlocks(blocks)), starts, ends, texts, atoms };
}

function hasBlockDescendant(element: Element): boolean {
  return element.querySelector('p, li, div') !== null;
}

/** The model the surface currently shows - `scanEditable` without the offsets. */
export function readRichText(root: HTMLElement): RichText {
  return scanEditable(root).document;
}

/**
 * Where the live selection sits, in plain characters, or null when it is not in
 * this surface.
 *
 * `from` is always the lower of the two: a selection dragged backwards is the
 * same range, and every operation here takes an ordered one.
 */
export function plainRangeOf(root: HTMLElement, scan: EditableScan): PlainRange | null {
  const selection = window.getSelection();
  if (selection === null || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) return null;
  const from = plainOffsetAt(scan, range.startContainer, range.startOffset);
  const to = plainOffsetAt(scan, range.endContainer, range.endOffset);
  if (from === null || to === null) return null;
  return { from: Math.min(from, to), to: Math.max(from, to) };
}

/** One DOM point as a plain offset, through the map `scanEditable` built. */
export function plainOffsetAt(scan: EditableScan, container: Node, offset: number): number | null {
  // A point INSIDE a pill is one of its two edges: a token has no middle.
  const atom = scan.atoms.find((one) => one.node === container || one.node.contains(container));
  if (atom !== undefined) return offset === 0 ? atom.start : atom.end;
  if (container.nodeType === Node.TEXT_NODE) {
    const start = scan.starts.get(container);
    if (start === undefined) return null;
    return start + Math.min(offset, (container.textContent ?? '').length);
  }
  const children = Array.from(container.childNodes);
  const child = children[offset];
  if (child !== undefined) return scan.starts.get(child) ?? null;
  const last = children[children.length - 1];
  if (last !== undefined) return scan.ends.get(last) ?? null;
  return scan.starts.get(container) ?? null;
}

/** The DOM point one plain offset names, clamped inside the surface. */
export function domPointAt(
  root: HTMLElement,
  scan: EditableScan,
  offset: number,
): { readonly node: Node; readonly offset: number } {
  let answer: { node: Node; offset: number } | null = null;
  for (const span of scan.texts) {
    const length = (span.node.textContent ?? '').length;
    if (offset >= span.start && offset <= span.start + length) {
      answer = { node: span.node, offset: offset - span.start };
    }
  }
  if (answer !== null) return answer;
  /*
    An offset on or inside a pill - which no text node covers - lands BESIDE
    it, in its parent: before it at its start, after it anywhere else. A pill
    at the very end of a paragraph is the common case, right after « { } ».
  */
  for (const atom of scan.atoms) {
    if (offset < atom.start || offset > atom.end) continue;
    const parent = atom.node.parentNode;
    if (parent === null) continue;
    const index = Array.from(parent.childNodes).indexOf(atom.node as ChildNode);
    return { node: parent, offset: offset === atom.start ? index : index + 1 };
  }
  const last = scan.texts[scan.texts.length - 1];
  if (last !== undefined && offset > last.start) {
    return { node: last.node, offset: (last.node.textContent ?? '').length };
  }
  const first = scan.texts[0];
  if (first !== undefined) return { node: first.node, offset: 0 };
  return { node: root, offset: 0 };
}

/**
 * Put the caret back where it was, after a repaint.
 *
 * `selectPlainRange` is the other half of `plainRangeOf`, and the two are what
 * make a toolbar press survive: the model changes, the surface is repainted
 * from it, and the selection is restored at the same two numbers.
 */
export function selectPlainRange(root: HTMLElement, range: PlainRange): void {
  const scan = scanEditable(root);
  const start = domPointAt(root, scan, range.from);
  const end = domPointAt(root, scan, range.to);
  const selection = window.getSelection();
  if (selection === null) return;
  const domRange = document.createRange();
  try {
    domRange.setStart(start.node, start.offset);
    domRange.setEnd(end.node, end.offset);
  } catch {
    // A node the repaint replaced: the caret goes to the end rather than
    // throwing, which would leave the surface un-editable until a click.
    return;
  }
  selection.removeAllRanges();
  selection.addRange(domRange);
}
